/**
 * The Reach Wire for M5 events (M5 spec §15.1; plan C7). One item per notable M5 event, with its cause:
 *
 * | Event (`EV`) | Item key(s) | Notes |
 * |---|---|---|
 * | `claimChanged` | `claimTaken`, `claimNew`, `claimLapsed` | the cause names the score component with the largest swing and its biggest event (`claimCauses`) |
 * | `stateStaged` | `statePending`, `stateActive`, `stateEnded` | ended = entering recovery; the global Isolation state is left to the gate items |
 * | `relationChanged` | `relation*` | escalation by the new rung, de-escalation by the rung reached; a war start whose yards cancelled each other's queued orders (`qty` > 0, M10 P4a3a) is `relationWarYards`, the one rollup item with the count |
 * | `stationDisabled`, `stationCaptured`, `zoneOpened` | same names | |
 * | `voteScheduled`, `voteResolved` | `voteBan`, `voteRepeal`, `banPassed`, ... | |
 * | `gateStage`, `gateClosure`, `gateReopened` | `gate*` | early text below the story tier, revealed text (with the number) from it |
 * | `guardianRaid` | `guardianRaid` | named **raiders** below outlaws.json `naming.nameTier` (spoiler canon) |
 * | `bountyChanged` | `bounty` | only a bounty the player's crime just raised (a `crime` event at the same time) |
 * | `sweep`, `tetherstoneSeized` | `sweep`, `sweepFailed`, `seizure` | |
 * | `clanReformed` | `clanReformed` | a disbanded clan re-forms (RD-B2, key `clans.reform`) |
 *
 * **Hidden events** (`varroStoke`, `holdfastFundsGuardians`) never make items; neither does any other event type.
 * Clan raids stay M4's `raid` items (`wire.ts`).
 *
 * **Naming rule** (M5 spec §9.2, §10): below the tier, the guardian faction is shown as the raiders (text and the
 * item's `factions`), and gate items use the early text, which the schema checks never names tetherstone or
 * guardians.
 *
 * **Gate** (M4 rules, shared): a guardian raid is a `raid` item and merges with raids in the same system within
 * wire.json `mergeDays`, like M4's. Every other M5 item is posted with kind `notice` until the protocol gains M5
 * kinds (`WIRE_KIND_OF`, D-TBD). An M5 item posts when fewer than wire.json `maxPerDay` items were posted today (any
 * kind), or `maxPerDay + localExtra` when it is local (the M4 rule), and always when its severity is 3.
 *
 * The text lives in `src/data/strategic/wire-m5.json` (parsed here once). Runs once per notable event and once per
 * faction day, never per agent, so it may allocate.
 */
import { z } from 'zod'
import wireM5Json from '../../data/strategic/wire-m5.json'
import { systemId as formatSystemId } from '../../galaxy/placement'
import { deriveSeed } from '../../galaxy/seed'
import { eventSlot, firstReadable, isHiddenEvent } from '../events'
import {
  CLAIM_COMPONENT_NAMES,
  EV,
  FACTION,
  FACTION_IDS,
  FACTION_NONE,
  FACTION_PLAYER,
  GATE_STAGE,
  GATE_STAGE_NAMES,
  MEASURE,
  RELATION,
  STAGE,
  STATE_KIND_NAMES,
  SYS_GLOBAL,
  type ClaimComponentName,
  type FactionId,
  type GateStageName,
  type StateKind,
  type StrategicState,
  type WireItem,
  type WireKind,
} from '../types'
import { WARE_COUNT, WARE_IDS } from '../wares'
import { jumpsBetween, stationName, systemName, wareName, type WireContext } from './causes'
import { pickVariant, render, type WireFields } from './templates'
import { NO_PLAYER, type PlayerWireContext } from './wire'

// ---------------------------------------------------------------------------------------------
// wire-m5.json
// ---------------------------------------------------------------------------------------------

/** Slots of M5 headlines. */
export const M5_WIRE_SLOTS = ['station', 'system', 'ware', 'faction', 'other', 'amount', 'duration', 'pilot', 'clan', 'cause'] as const
const CAUSE_SLOTS = M5_WIRE_SLOTS.filter((s) => s !== 'cause')

/** Keys of `templates` (the items with one template list). */
export const M5_TEMPLATE_KEYS = [
  'claimTaken',
  'claimNew',
  'claimLapsed',
  'relationEconomic',
  'relationSkirmish',
  'relationWar',
  'relationEasedEconomic',
  'relationEasedSkirmish',
  'relationPeace',
  'stationDisabled',
  'stationCaptured',
  'zoneOpened',
  'voteBan',
  'voteRepeal',
  'banPassed',
  'banFailed',
  'repealPassed',
  'repealFailed',
  'bounty',
  'sweep',
  'sweepFailed',
  'seizure',
  'clanReformed',
  'relationWarYards',
] as const
export type M5TemplateKey = (typeof M5_TEMPLATE_KEYS)[number]

/** Gate item keys (`gate.early` / `gate.revealed`). */
export const M5_GATE_KEYS = ['gateNormal', 'gateFlickers', 'gateTremors', 'gateClosed', 'gateClosure', 'gateReopened', 'gateChatter'] as const
export type M5GateKey = (typeof M5_GATE_KEYS)[number]

export const M5_STATE_KEYS = ['statePending', 'stateActive', 'stateEnded'] as const
export type M5StateKey = (typeof M5_STATE_KEYS)[number]

/** Every M5 Wire item key (the severity table). */
export const M5_WIRE_KEYS = [...M5_TEMPLATE_KEYS, ...M5_STATE_KEYS, ...M5_GATE_KEYS, 'guardianRaid'] as const
export type M5WireKey = (typeof M5_WIRE_KEYS)[number]

/** State kinds with system items (Isolation is global: the gate items report it). */
export const M5_WIRE_STATE_KINDS = ['boom', 'bust', 'famine', 'strike', 'lockdown', 'blockade', 'war'] as const satisfies readonly StateKind[]

/** Words the early (below-tier) gate and guardian-raid text must never contain (spoiler canon, M5 spec §9.2, §10). */
export const EARLY_FORBIDDEN = ['tetherstone', 'guardian', 'resonance', 'holdfast'] as const

const EV_NAMES = Object.keys(EV) as [keyof typeof EV, ...(keyof typeof EV)[]]

function slotsOk(text: string, slots: readonly string[]): boolean {
  for (const m of text.matchAll(/\{([^}]*)\}/g)) if (!slots.includes(m[1])) return false
  return true
}
const text = (slots: readonly string[]): z.ZodType<string> =>
  z.string().min(1).refine((t) => slotsOk(t, slots), { message: `unknown {slot}; allowed: ${slots.join(', ')}` })
const variants = (slots: readonly string[]): z.ZodType<string[]> => z.array(text(slots)).min(1).max(4)
const earlyVariants = (slots: readonly string[]): z.ZodType<string[]> =>
  variants(slots).refine((vs) => vs.every((v) => EARLY_FORBIDDEN.every((w) => !v.toLowerCase().includes(w))), {
    message: `early text must not name ${EARLY_FORBIDDEN.join(', ')}`,
  })
const severity = z.union([z.literal(1), z.literal(2), z.literal(3)])

export const m5WireConfigSchema = z.strictObject({
  comment: z.string(),
  names: z.record(z.enum(FACTION_IDS), z.string().min(1)),
  unknownFaction: z.string().min(1),
  pilot: z.string().min(1),
  severity: z.record(z.enum(M5_WIRE_KEYS), severity),
  templates: z.record(z.enum(M5_TEMPLATE_KEYS), variants(M5_WIRE_SLOTS)),
  claimCauses: z.record(
    z.enum(CLAIM_COMPONENT_NAMES),
    z.strictObject({ default: variants(CAUSE_SLOTS), events: z.partialRecord(z.enum(EV_NAMES), variants(CAUSE_SLOTS)) }),
  ),
  lapseCauses: z.record(z.enum(CLAIM_COMPONENT_NAMES), variants(CAUSE_SLOTS)),
  states: z.record(
    z.enum(M5_WIRE_STATE_KINDS),
    z.strictObject({ pending: variants(M5_WIRE_SLOTS), active: variants(M5_WIRE_SLOTS), ended: variants(M5_WIRE_SLOTS) }),
  ),
  gate: z.strictObject({
    early: z.record(z.enum(M5_GATE_KEYS), earlyVariants(M5_WIRE_SLOTS.filter((s) => s !== 'amount'))),
    revealed: z.record(z.enum(M5_GATE_KEYS), variants(M5_WIRE_SLOTS)),
  }),
  guardianRaid: z.strictObject({ early: earlyVariants(M5_WIRE_SLOTS), revealed: variants(M5_WIRE_SLOTS) }),
  gateStatus: z.record(z.enum(GATE_STAGE_NAMES), text([])),
})
export type M5WireConfig = z.infer<typeof m5WireConfigSchema>

/** The parsed wire-m5.json. D-TBD: fold into wire.json (`m5`) and `StrategicData` when the schema widens. */
export const M5_WIRE: M5WireConfig = m5WireConfigSchema.parse(wireM5Json)

// ---------------------------------------------------------------------------------------------
// Context, naming and posting
// ---------------------------------------------------------------------------------------------

/** What the M5 Wire reads and writes: the M4 Wire state plus the M5 stores it names things from. */
export type M5WireState = WireContext & Pick<StrategicState, 'counters' | 'wire' | 'claims' | 'clans' | 'storyTier' | 'gate' | 'gateStage' | 'corp'>

/** The player's side: M4's relevance context, plus the name bounties are posted on (default wire-m5.json `pilot`). */
export interface M5PlayerContext extends PlayerWireContext {
  pilot?: string
}

/**
 * The protocol kind an M5 item is stored with. Guardian raids are raids; the rest are `notice` until `WireKind`
 * gains M5 kinds (D-TBD: M5 Wire kinds; the gate maps them 1:1 here).
 */
export function wireKindOf(key: M5WireKey): WireKind {
  return key === 'guardianRaid' ? 'raid' : 'notice'
}

/** The story tier from which guardians are named (outlaws.json `naming.nameTier`). */
export function guardianNameTier(state: Pick<StrategicState, 'data'>): number {
  return state.data.charter.outlaws.naming.nameTier
}

/** The story tier from which the gate's number is shown (gate.json `stages.visibleTier`). */
export function gateVisibleTier(state: Pick<StrategicState, 'data'>): number {
  return state.data.gate.stages.visibleTier
}

/** The faction the player may see: guardians are shown as the raiders below the naming tier. */
export function publicFaction(state: Pick<StrategicState, 'data' | 'storyTier'>, f: number): number {
  return f === FACTION.guardian && state.storyTier < guardianNameTier(state) ? FACTION.raider : f
}

/** The public faction id of a faction code, or null (none, the player). */
export function publicFactionId(state: Pick<StrategicState, 'data' | 'storyTier'>, f: number): FactionId | null {
  const p = publicFaction(state, f)
  return p >= 0 && p < FACTION_IDS.length ? FACTION_IDS[p] : null
}

/**
 * A faction's short headline name ("Tethys", "the Authority"), after the naming rule. The player corporation's
 * slot reads the corporation's name (D-424, §27.7: claim gained, lost and flipped items name it), not "local forces".
 */
export function m5FactionName(state: Pick<StrategicState, 'data' | 'storyTier' | 'corp'>, f: number, pilot = M5_WIRE.pilot): string {
  if (f === FACTION_PLAYER) return pilot
  if (f >= 0 && f === state.corp.faction && state.corp.name.length > 0) return state.corp.name
  const id = publicFactionId(state, f)
  return id === null ? M5_WIRE.unknownFaction : M5_WIRE.names[id]
}

/** The gate status text below the tier ("Gate operating", "Transit delays", "Tremors", "Closed"). */
export function gateStatusText(stage: GateStageName): string {
  return M5_WIRE.gateStatus[stage]
}

/** A whole number with thousands separators ("15,000"), independent of the locale. */
export function formatAmount(x: number): string {
  const r = Math.round(Math.abs(x))
  const s = String(r).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return x < 0 && r > 0 ? `-${s}` : s
}

/** A closure length: minutes below 90 min, else whole hours ("2 h"). */
export function formatDuration(seconds: number): string {
  const s = seconds > 0 ? seconds : 0
  return s < 5400 ? `${Math.max(1, Math.round(s / 60))} min` : `${Math.round(s / 3600)} h`
}

function capitalize(s: string): string {
  return s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s
}

function homeSys(state: Pick<WireContext, 'galaxy'>): number {
  return state.galaxy.systems.findIndex((s) => s.id === state.galaxy.homeSystemId)
}

/** The system an item is placed in: the event's, or the home (gate) system for Reach-wide events. */
function placeSys(state: Pick<WireContext, 'galaxy'>, sys: number): number {
  return sys === SYS_GLOBAL || sys < 0 || sys >= state.galaxy.systems.length ? homeSys(state) : sys
}

function systemIdOf(state: Pick<WireContext, 'galaxy'>, sys: number): string {
  if (sys < 0) return state.galaxy.homeSystemId
  return state.galaxy.systems[sys]?.id ?? formatSystemId(sys)
}

function itemsToday(state: Pick<StrategicState, 'wire' | 'day'>): number {
  let n = 0
  for (let i = state.wire.length - 1; i >= 0; i--) {
    const item = state.wire[i]
    if (item.day < state.day) break
    if (item.day === state.day) n++
  }
  return n
}

function mergesRaid(state: M5WireState, systemId: string): boolean {
  const from = state.time - state.data.wire.mergeDays * state.data.strategic.clock.dayS
  for (let i = state.wire.length - 1; i >= 0; i--) {
    const item = state.wire[i]
    if (item.t < from) break
    if (item.kind === 'raid' && item.stationId === undefined && item.ware === undefined && item.systemId === systemId) return true
  }
  return false
}

function isLocal(state: M5WireState, player: PlayerWireContext, sys: number, ware: number): boolean {
  if (player.sys >= 0 && sys >= 0 && jumpsBetween(state.routing, player.sys, sys) <= state.data.wire.localJumps) return true
  return ware >= 0 && player.wares.includes(ware)
}

interface M5Post {
  key: M5WireKey
  variants: readonly string[]
  /** The event's system (SYS_GLOBAL for Reach-wide events). */
  sys: number
  station: number
  /** The ware the item is filed under (the "My cargo" filter), −1 = none. */
  ware: number
  faction: number
  other: number
  fields: WireFields
}

/** Gate, rate, render and append one M5 item. Returns it, or null (merged or over the rate). */
function post(state: M5WireState, p: M5Post, player: PlayerWireContext, out: WireItem[] | undefined): WireItem | null {
  const sys = placeSys(state, p.sys)
  const systemId = systemIdOf(state, sys)
  const kind = wireKindOf(p.key)
  const sev = M5_WIRE.severity[p.key]
  if (kind === 'raid' && mergesRaid(state, systemId)) return null
  if (sev < 3) {
    const today = itemsToday(state)
    const w = state.data.wire
    if (!(today < w.maxPerDay || (today < w.maxPerDay + w.localExtra && isLocal(state, player, sys, p.ware)))) return null
  }
  const id = state.counters.wireSeq
  const headline = capitalize(render(pickVariant(p.variants, state.seed, 'wire', id), p.fields))
  const factions: FactionId[] = []
  const f1 = publicFactionId(state, p.faction)
  const f2 = publicFactionId(state, p.other)
  if (f1 !== null) factions.push(f1)
  if (f2 !== null && f2 !== f1) factions.push(f2)
  const item: WireItem = { id, t: state.time, day: state.day, kind, severity: sev, systemId, headline, factions }
  if (p.station >= 0 && p.station < state.stations.count) item.stationId = state.stations.id[p.station]
  if (p.ware >= 0 && p.ware < WARE_COUNT) item.ware = WARE_IDS[p.ware]
  state.counters.wireSeq = id + 1
  state.wire.push(item)
  const excess = state.wire.length - state.data.strategic.wireKeep
  if (excess > 0) state.wire.splice(0, excess)
  if (out !== undefined) out.push(item)
  return item
}

// ---------------------------------------------------------------------------------------------
// Causes
// ---------------------------------------------------------------------------------------------

const EV_NAME_OF: readonly string[] = (() => {
  const names: string[] = []
  for (const name of EV_NAMES) names[EV[name]] = name
  return names
})()

/** A claim change's cause (M5 spec §15.1): the component with the largest swing and the EV name of its biggest event (null = none). */
export interface ClaimCause {
  component: ClaimComponentName
  event: string | null
  text: string
}

/**
 * The cause phrase of the `claimChanged` event at notable seq `seq` (" as its haulers outsupply the Varos
 * Refinery"), with the variant of Wire item `id`. Null when the event is not a readable claim change.
 */
export function claimCause(state: M5WireState, seq: number, id = state.counters.wireSeq): ClaimCause | null {
  const ring = state.notable
  if (seq < firstReadable(ring, 0) || seq >= ring.head) return null
  const k = eventSlot(ring, seq)
  if (ring.type[k] !== EV.claimChanged) return null
  const component = CLAIM_COMPONENT_NAMES[ring.cause[k]] ?? 'ops'
  const lapse = ring.qty[k] === 1 || ring.faction[k] === FACTION_NONE
  const event = ring.aux[k] > 0 ? (EV_NAME_OF[ring.aux[k]] ?? null) : null
  let phrases: readonly string[]
  if (lapse) phrases = M5_WIRE.lapseCauses[component]
  else {
    const table = M5_WIRE.claimCauses[component]
    const specific = event !== null ? table.events[event as keyof typeof EV] : undefined
    phrases = specific ?? table.default
  }
  const sys = ring.sys[k]
  const fields: WireFields = {
    station: stationName(state, ring.station[k], sys),
    system: systemName(state, sys),
    faction: m5FactionName(state, ring.faction[k]),
    other: m5FactionName(state, ring.other[k]),
  }
  return { component, event, text: render(pickVariant(phrases, state.seed, 'wireCause', id), fields) }
}

// ---------------------------------------------------------------------------------------------
// Event items
// ---------------------------------------------------------------------------------------------

/** The relation item's key; a war start whose yards cancelled orders (`qty` > 0, M10 P4a3a) is `relationWarYards`. */
function relationKey(from: number, to: number, cancelled: number): M5TemplateKey | null {
  if (to > from) {
    if (to === RELATION.war) return cancelled > 0 ? 'relationWarYards' : 'relationWar'
    return to === RELATION.skirmish ? 'relationSkirmish' : 'relationEconomic'
  }
  if (to < from) return to === RELATION.peace ? 'relationPeace' : to === RELATION.economic ? 'relationEasedEconomic' : 'relationEasedSkirmish'
  return null
}

const GATE_KEY_OF_STAGE: Record<GateStageName, M5GateKey> = {
  normal: 'gateNormal',
  flickers: 'gateFlickers',
  tremors: 'gateTremors',
  closed: 'gateClosed',
}

function gateVariants(state: M5WireState, key: M5GateKey): readonly string[] {
  return state.storyTier >= gateVisibleTier(state) ? M5_WIRE.gate.revealed[key] : M5_WIRE.gate.early[key]
}

/** The crime at the same time as the bounty event `seq` in jurisdiction `f` (scanning back at most 8 events), or −1. */
function crimeBefore(ring: M5WireState['notable'], seq: number, f: number): number {
  const k0 = eventSlot(ring, seq)
  const t = ring.t[k0]
  const first = firstReadable(ring, 0)
  for (let i = seq - 1; i >= first && i >= seq - 8; i--) {
    const k = eventSlot(ring, i)
    if (ring.t[k] !== t) break
    if (ring.type[k] === EV.crime && ring.faction[k] === f) return k
  }
  return -1
}

function clanName(state: M5WireState, clan: number): string {
  const names = state.data.charter.outlaws.clans.names
  if (clan < 0 || clan >= state.clans.count) return M5_WIRE.names.raider
  return names[state.clans.nameIndex[clan]] ?? M5_WIRE.names.raider
}

/**
 * The M5 item of notable event `seq`, or null: another or a hidden type, unreadable, a transition with no item,
 * merged, or over the rate.
 */
export function immediateM5Wire(state: M5WireState, seq: number, player: M5PlayerContext = NO_PLAYER, out?: WireItem[]): WireItem | null {
  const ring = state.notable
  if (seq < firstReadable(ring, 0) || seq >= ring.head) return null
  const k = eventSlot(ring, seq)
  const type = ring.type[k]
  if (isHiddenEvent(type)) return null
  const sys = ring.sys[k]
  const station = ring.station[k] >= 0 && ring.station[k] < state.stations.count ? ring.station[k] : -1
  const f = ring.faction[k]
  const o = ring.other[k]
  const pilot = player.pilot ?? M5_WIRE.pilot
  const names = (): WireFields => ({
    station: station >= 0 ? stationName(state, station, sys) : '',
    system: systemName(state, placeSys(state, sys)),
    faction: m5FactionName(state, f, pilot),
    other: m5FactionName(state, o, pilot),
  })
  const item = (key: M5WireKey, variants: readonly string[], fields: WireFields, ware = -1): WireItem | null =>
    post(state, { key, variants, sys, station, ware, faction: f, other: o, fields }, player, out)

  switch (type) {
    case EV.claimChanged: {
      const lapse = ring.qty[k] === 1 || f === FACTION_NONE
      const key: M5TemplateKey = lapse ? 'claimLapsed' : o === FACTION_NONE ? 'claimNew' : 'claimTaken'
      const cause = claimCause(state, seq)
      return item(key, M5_WIRE.templates[key], { ...names(), station: '', cause: cause?.text ?? '' })
    }
    case EV.stateStaged: {
      const kind = STATE_KIND_NAMES[ring.cause[k]]
      if (sys === SYS_GLOBAL || kind === undefined || kind === 'isolation') return null
      const stage = ring.qty[k]
      const which = stage === STAGE.pending ? 'pending' : stage === STAGE.active ? 'active' : stage === STAGE.recovering ? 'ended' : null
      if (which === null) return null
      const key: M5StateKey = which === 'pending' ? 'statePending' : which === 'active' ? 'stateActive' : 'stateEnded'
      const holder = sys < state.claims.systems ? state.claims.holder[sys] : -1
      const h = holder >= 0 ? holder : FACTION_NONE
      return post(
        state,
        { key, variants: M5_WIRE.states[kind][which], sys, station: -1, ware: -1, faction: h, other: FACTION_NONE, fields: { system: systemName(state, sys), faction: m5FactionName(state, h) } },
        player,
        out,
      )
    }
    case EV.relationChanged: {
      const cancelled = ring.qty[k]
      const key = relationKey(ring.aux[k], ring.cause[k], cancelled)
      if (key === null) return null
      return item(key, M5_WIRE.templates[key], key === 'relationWarYards' ? { ...names(), amount: formatAmount(cancelled) } : names())
    }
    case EV.stationDisabled:
      return station < 0 ? null : item('stationDisabled', M5_WIRE.templates.stationDisabled, names())
    case EV.stationCaptured:
      return station < 0 ? null : item('stationCaptured', M5_WIRE.templates.stationCaptured, names())
    case EV.zoneOpened:
      return item('zoneOpened', M5_WIRE.templates.zoneOpened, names())
    case EV.voteScheduled: {
      const key: M5TemplateKey = ring.cause[k] === MEASURE.tetherstoneRepeal ? 'voteRepeal' : 'voteBan'
      return item(key, M5_WIRE.templates[key], names())
    }
    case EV.voteResolved: {
      const passed = ring.qty[k] === 1
      const repeal = ring.cause[k] === MEASURE.tetherstoneRepeal
      const key: M5TemplateKey = repeal ? (passed ? 'repealPassed' : 'repealFailed') : passed ? 'banPassed' : 'banFailed'
      return item(key, M5_WIRE.templates[key], names())
    }
    case EV.gateStage: {
      const stage = GATE_STAGE_NAMES[ring.cause[k]]
      if (stage === undefined || ring.cause[k] === ring.aux[k]) return null
      const key = GATE_KEY_OF_STAGE[stage]
      return post(state, { key, variants: gateVariants(state, key), sys: SYS_GLOBAL, station: -1, ware: -1, faction: FACTION_NONE, other: FACTION_NONE, fields: { amount: formatAmount(ring.value[k]) } }, player, out)
    }
    case EV.gateClosure:
      return post(
        state,
        { key: 'gateClosure', variants: gateVariants(state, 'gateClosure'), sys: SYS_GLOBAL, station: -1, ware: -1, faction: FACTION_NONE, other: FACTION_NONE, fields: { amount: formatAmount(ring.value[k]), duration: formatDuration(ring.qty[k]) } },
        player,
        out,
      )
    case EV.gateReopened:
      return post(
        state,
        { key: 'gateReopened', variants: gateVariants(state, 'gateReopened'), sys: SYS_GLOBAL, station: -1, ware: -1, faction: FACTION_NONE, other: FACTION_NONE, fields: { amount: formatAmount(ring.value[k]) } },
        player,
        out,
      )
    case EV.guardianRaid: {
      const revealed = state.storyTier >= guardianNameTier(state)
      const w = ring.ware[k]
      return item('guardianRaid', revealed ? M5_WIRE.guardianRaid.revealed : M5_WIRE.guardianRaid.early, { ...names(), ware: revealed && w >= 0 ? wareName(state, w) : '' })
    }
    case EV.bountyChanged: {
      if (f >= state.data.charter.factionCount || state.data.charter.outlaw[f] === 1 || !(ring.value[k] > 0)) return null
      const crime = crimeBefore(ring, seq, f)
      if (crime < 0) return null
      const crimeSys = ring.sys[crime]
      return post(
        state,
        { key: 'bounty', variants: M5_WIRE.templates.bounty, sys: crimeSys, station: -1, ware: -1, faction: f, other: FACTION_NONE, fields: { faction: m5FactionName(state, f), amount: formatAmount(ring.value[k]), pilot } },
        player,
        out,
      )
    }
    case EV.sweep: {
      const key: M5TemplateKey = ring.qty[k] === 1 ? 'sweep' : 'sweepFailed'
      return item(key, M5_WIRE.templates[key], { ...names(), clan: clanName(state, ring.aux[k]) })
    }
    case EV.clanReformed:
      return item('clanReformed', M5_WIRE.templates.clanReformed, { ...names(), clan: clanName(state, ring.aux[k]) })
    case EV.tetherstoneSeized: {
      const w = ring.ware[k]
      return item('seizure', M5_WIRE.templates.seizure, { ...names(), ware: w >= 0 ? wareName(state, w) : '', amount: formatAmount(ring.qty[k]) }, w)
    }
    default:
      return null
  }
}

/** Posts the M5 items of every notable event from `cursor` on. Returns the new cursor (`notable.head`). */
export function scanM5Wire(state: M5WireState, cursor: number, player: M5PlayerContext = NO_PLAYER, out?: WireItem[]): number {
  const ring = state.notable
  for (let i = firstReadable(ring, cursor); i < ring.head; i++) immediateM5Wire(state, i, player, out)
  return ring.head
}

/**
 * The M5 day items (run with M4's day Wire, slice 15): gate chatter while the gate flickers, with gate.json
 * `flicker.chatterChance` on a roll seeded by the day. Returns the number of items posted.
 */
export function generateM5DayWire(state: M5WireState, player: M5PlayerContext = NO_PLAYER, out?: WireItem[]): number {
  if (state.gateStage.stage !== GATE_STAGE.flickers) return 0
  const roll = deriveSeed(state.seed, 'gateChatter', state.day) / 4294967296
  if (!(roll < state.data.gate.stages.flicker.chatterChance)) return 0
  const posted = post(
    state,
    { key: 'gateChatter', variants: gateVariants(state, 'gateChatter'), sys: SYS_GLOBAL, station: -1, ware: -1, faction: FACTION_NONE, other: FACTION_NONE, fields: { amount: formatAmount(state.gate.stability) } },
    player,
    out,
  )
  return posted === null ? 0 : 1
}
